import React from 'react';
import { Link } from 'react-router-dom';
import { FaLayerGroup, FaArrowRight, FaCamera, FaLungs, FaEye, FaWalking, FaStethoscope, FaHeartbeat, FaBaby } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

/**
 * A modality card.
 *
 * These used to carry stock photographs, and the photos never actually
 * depicted the modality — a generic lab bench standing in for a fundus screen,
 * a gloved hand for dermatology. A near-miss image is worse than none on a page
 * shown to clinicians and funders: it invites the reader to wonder what else is
 * approximate. A typed icon on a branded panel states exactly what the card is
 * about and cannot be wrong.
 */
const DemoOption = ({ title, description, icon, link, gradient, accent }) => {
  return (
    <div className={`bg-gradient-to-br rounded-xl overflow-hidden border border-gray-800 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/10 hover:border-purple-500/50 ${gradient}`}>
      <div className={`h-48 relative overflow-hidden flex items-center justify-center bg-gradient-to-br ${accent}`}>
        <div className="text-white/20 text-8xl">{icon}</div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/30 to-transparent"></div>
        <h3 className="absolute bottom-4 left-6 right-4 text-2xl font-bold text-white">{title}</h3>
      </div>
      <div className="p-6">
        <p className="text-gray-300 mb-6">{description}</p>
        <Link 
          to={link} 
          className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium inline-block"
        >
          Try Demo
        </Link>
      </div>
    </div>
  );
};

const Demo = () => {
  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-10 page-transition">
        <div className="container-custom">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-500 to-indigo-400">
              Experience Our AI in Action
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Try our interactive demos to see how our AI solutions can transform healthcare delivery with accurate diagnostics in seconds.
            </p>
          </div>
          
          {/* Featured: the encounter workflow. Led separately from the
              modality grid because it is a different shape of task — one
              patient visit, several images, one triage decision — and listing
              it as an eighth tile would frame it as just another scan type. */}
          <Link
            to="/analyze/encounter"
            className="group block mb-8 rounded-2xl overflow-hidden border border-purple-700/60 bg-gradient-to-r from-purple-900/40 to-indigo-900/30 hover:border-purple-500 transition-all duration-300"
          >
            <div className="p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="p-5 bg-purple-900/50 rounded-2xl group-hover:bg-purple-800/60 transition-colors shrink-0">
                <FaLayerGroup className="h-10 w-10 text-purple-300" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <h3 className="text-2xl font-bold text-white">Screening Encounter</h3>
                  <span className="px-3 py-1 text-xs bg-purple-900/60 text-purple-200 border border-purple-600/50 rounded-full">
                    Flagship workflow
                  </span>
                </div>
                <p className="text-gray-300 mb-3">
                  How screening actually happens: a health worker photographs
                  both eyes and both feet in one visit and types what the patient
                  reported. Analysed together rather than separately, so
                  left-right asymmetry, findings that corroborate across organs,
                  and your clinical notes all feed one triage decision.
                </p>
                <p className="text-sm text-purple-300/90 flex items-center gap-2">
                  <FaCamera className="inline" /> Capture straight from the camera, or upload files
                </p>
              </div>
              <FaArrowRight className="h-6 w-6 text-purple-400 group-hover:translate-x-1 transition-transform shrink-0" />
            </div>
          </Link>

          {/* Single-image demos — every modality the platform actually runs */}
          <h2 className="text-2xl font-bold mb-6 text-white">Or try a single scan</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            <DemoOption
              title="Chest X-Ray"
              description="Screens for 18 pathologies including pneumonia and tuberculosis, and returns a heatmap showing which region drove the finding."
              icon={<FaLungs />}
              accent="from-indigo-600 to-indigo-950"
              link="/analyze/xray"
              gradient="from-purple-900/20 to-indigo-900/20"
            />

            <DemoOption
              title="Retinal Fundus"
              description="Screens a fundus photograph for eight conditions. Cataract, myopia and glaucoma are our strongest results; diabetic retinopathy is still below our deployment bar and we say so on the result."
              icon={<FaEye />}
              accent="from-teal-600 to-emerald-950"
              link="/analyze/retina"
              gradient="from-teal-900/20 to-emerald-900/20"
            />

            <DemoOption
              title="Diabetic Foot"
              description="Classifies a foot photograph into ulcer types — neuropathic, ischemic, infected, pre-ulcer callus — and is the one modality where your typed clinical context changes the model's own reading."
              icon={<FaWalking />}
              accent="from-amber-600 to-orange-950"
              link="/analyze/foot"
              gradient="from-amber-900/20 to-orange-900/20"
            />

            <DemoOption
              title="Skin Lesion"
              description="Two models run on every photo and their results are merged: one trained on biopsy-confirmed cancer subtypes, one on 44 general conditions with far better skin-tone diversity."
              icon={<FaStethoscope />}
              accent="from-fuchsia-600 to-purple-950"
              link="/analyze/skin"
              gradient="from-fuchsia-900/20 to-purple-900/20"
            />

            <DemoOption
              title="Breast Ultrasound"
              description="Classifies a breast ultrasound image as benign, malignant or normal tissue, with a confidence score and a flag when the model is unsure."
              icon={<FaHeartbeat />}
              accent="from-blue-600 to-indigo-950"
              link="/analyze/ct-scan"
              gradient="from-indigo-900/20 to-blue-900/20"
            />

            <DemoOption
              title="Fetal Ultrasound Plane"
              description="A scan-quality check: did this frame capture the standard plane needed for biometry? It checks the view, not the baby's health — a deliberately narrower claim."
              icon={<FaBaby />}
              accent="from-pink-600 to-rose-950"
              link="/analyze/fetal-plane"
              gradient="from-pink-900/20 to-rose-900/20"
            />
          </div>

          {/* How It Works */}
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-16">
            <h2 className="text-3xl font-bold mb-10 text-center">How the Demo Works</h2>
            
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-600 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-xl font-bold">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Select Demo Type</h3>
                <p className="text-gray-300">Run a full screening encounter, or a single scan: chest X-ray, retina, diabetic foot, skin, breast ultrasound or fetal plane.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-600 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-xl font-bold">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Capture or Upload</h3>
                <p className="text-gray-300">Photograph directly with your device camera, or upload an existing image. Add patient context if you have it.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-600 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-xl font-bold">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">AI Analysis</h3>
                <p className="text-gray-300">Our AI will process the image in seconds, analyzing it for various medical conditions.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-600 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-xl font-bold">4</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">View Results</h3>
                <p className="text-gray-300">Explore the detailed analysis with findings, confidence scores, and visual highlights.</p>
              </div>
            </div>
          </div>
          
          {/* Demo Disclaimer */}
          <div className="bg-gray-900/50 border-l-4 border-purple-500 p-6 rounded-r-lg mb-16">
            <h3 className="text-xl font-semibold mb-3 flex items-center">
              <svg className="w-6 h-6 text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              Demo Disclaimer
            </h3>
            <p className="text-gray-300 mb-4">
              These demos are provided for educational and demonstration purposes only. The AI analyses are not intended for clinical use or to replace 
              professional medical advice, diagnosis, or treatment.
            </p>
            <p className="text-gray-300">
              While our demos use the same AI technology that powers our clinical solutions, the clinical versions undergo additional 
              regulatory approvals and validations before deployment in healthcare settings.
            </p>
          </div>
          
          {/* Testimonials */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-10 text-center">What Users Say About Our Demos</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-gray-900 to-gray-900/80 p-6 rounded-xl shadow-xl relative">
                <div className="absolute -top-2 -left-2 text-5xl text-purple-600 opacity-50">"</div>
                <p className="text-gray-300 mb-6 relative z-10">
                  I was blown away by the speed and accuracy of the chest X-ray analysis. As a radiologist, I'm impressed by how detailed the findings are and how well the AI highlights areas of concern.
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-purple-700 mr-4"></div>
                  <div>
                    <h4 className="font-medium text-white">Dr. Michael Chen</h4>
                    <p className="text-sm text-gray-400">Radiologist, Johns Hopkins Hospital</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-900 to-gray-900/80 p-6 rounded-xl shadow-xl relative">
                <div className="absolute -top-2 -left-2 text-5xl text-purple-600 opacity-50">"</div>
                <p className="text-gray-300 mb-6 relative z-10">
                  The skin lesion detection demo is incredibly intuitive. The visualization tools and detailed breakdown of the analysis give me confidence in the AI's assessment of potentially concerning lesions.
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-indigo-700 mr-4"></div>
                  <div>
                    <h4 className="font-medium text-white">Dr. Sarah Johnson</h4>
                    <p className="text-sm text-gray-400">Dermatologist, Mayo Clinic</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-900 to-gray-900/80 p-6 rounded-xl shadow-xl relative">
                <div className="absolute -top-2 -left-2 text-5xl text-purple-600 opacity-50">"</div>
                <p className="text-gray-300 mb-6 relative z-10">
                  The Ultrasound interpretation demo detected a subtle hemorrhage that I initially missed. This tool would be invaluable in emergency settings where time is critical and radiologists might not be immediately available.
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-purple-700 mr-4"></div>
                  <div>
                    <h4 className="font-medium text-white">Dr. David Okafor</h4>
                    <p className="text-sm text-gray-400">Emergency Physician, Kenyatta Hospital</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-2xl p-10 text-center border border-purple-500/30">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience the Full Solution?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Our demos showcase just a fraction of what our full AI solutions can do. Contact us to learn more about our comprehensive healthcare AI platform.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link 
                to="/contact" 
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-medium"
              >
                Contact Sales
              </Link>
              <Link 
                to="/solutions" 
                className="px-8 py-3 bg-black border border-purple-500 text-purple-400 hover:bg-purple-900/20 rounded-lg font-medium"
              >
                Explore Solutions
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Demo;